import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@cms/components/data/Button';
import { ALLOWED_LIMITS, INPUT_VARIANTS } from '@cms/utils/constants';
import { generateSearchParam } from '@cms/utils/helpers';
import { ISystemAsset, SUBTYPES } from '@dl/declarations';
import { ImageAsset } from '../../components/data/Asset';
import { AssetForm } from '../../components/forms/AssetForm';
import { DataPart } from '../../components/ui/DataPart';
import PageContainer from '../../components/ui/PageContainer';
import { AssetsPath, renderRouteOptions, Routes } from '../../utils/routes';

import { FiExternalLink } from "react-icons/fi";

interface AssetsPageProps extends Partial<React.ComponentProps<typeof DataPart>> {
    renderItem?: (row: ISystemAsset) => React.ReactNode;
}

export const AssetsPageContent: React.FC<AssetsPageProps> = (pageProps) => {
    const { renderItem, ...restOfPageProps } = pageProps;

    const [, setSearchParams] = useSearchParams()

    const basicRenderItem = useCallback((row: ISystemAsset) => {
        switch (row.mime_type) {
            case 'image':
                return (
                    <ImageAsset
                        asset={row}
                        actions={{
                            useImage: {
                                show: false,
                            },
                            editImage: {
                                show: true,
                            },
                        }}
                    />
                )

            case 'video':
                return null

            default:
                return null
        }
    }, [])

    const handleUploadClick = useCallback(() => {
        setSearchParams((prevParams) => {
            prevParams.set(generateSearchParam(AssetsPath, 'mode'), 'create')
            return prevParams
        })
    }, [setSearchParams])

    // useUnmount(() => {
    //     setSearchParams((prevParams) => {
    //         const allParams = Array.from(prevParams.entries())

    //         allParams.forEach(([key, _value]) => {
    //             if (
    //                 key.startsWith(generateSearchParam(AssetsPath, ''))
    //                 &&
    //                 key !== generateSearchParam(AssetsPath, 'subtypes')
    //             ) {
    //                 prevParams.delete(key)
    //             }
    //         })

    //         return prevParams
    //     })
    // })

    return (
        <DataPart<ISystemAsset>
            pageSize={ALLOWED_LIMITS["20"]}
            tableSize='full'
            distribution={{
                type: 'grid',
                renderItem: renderItem || basicRenderItem,
                noOfColumns: '5'
            }}

            queryOptions={{
                basePath: AssetsPath,
                initialParams: {
                    sort: 'created_at:desc',
                },
            }}

            updateOptions={{
                allow: true,
                hide: true
            }}
            createOptions={{
                allow: true,
                component: (
                    <Button
                        as='button'
                        inputVariant="white_text_blue_bg"
                        rightIcon={<FiExternalLink size={24} />}
                        onClick={handleUploadClick}
                        className={clsx(
                            INPUT_VARIANTS['common_text']
                        )}
                    >
                        Upload
                    </Button>
                )
            }}
            searchOptions={{
                allow: true,
            }}

            form={<AssetForm />}

            filters={{
                subtypes: {
                    type: 'select',
                    label: 'Subtypes',
                    allowMultiSelect: true,
                    options: [],
                    hide: true,
                },
                type: {
                    type: 'select',
                    label: 'All categories',
                    allowMultiSelect: false,
                    options: [
                        // { _id: 'all', slug: 'all', label: 'All categories', groupKey: 'Options' },
                        { _id: '2', slug: 'illustration', label: 'Illustration', groupKey: 'Options' },
                        { _id: '3', slug: 'stock', label: 'Stock', groupKey: 'Options' },
                        { _id: '4', slug: 'chainwire', label: 'Chainwire', groupKey: 'Options' },
                    ],
                    searchOptions: {
                        allow: false
                    }
                },
                keywords: {
                    type: 'static',
                    label: 'Keywords',
                },
                sort: {
                    type: 'select',
                    label: 'Sort by',
                    allowMultiSelect: false,
                    options: [
                        { _id: '1', slug: 'main_asset_story_aggregate:desc', label: 'Most Used', groupKey: 'Options' },
                        { _id: '2', slug: 'last_used_at:desc', label: 'Recently Used', groupKey: 'Options' },
                        { _id: '3', slug: 'last_used_at:asc', label: 'Not Used Recently', groupKey: 'Options' },
                        { _id: '4', slug: 'created_at:desc', label: 'Newest Created', groupKey: 'Options' },
                        { _id: '5', slug: 'created_at:asc', label: 'Oldest Created', groupKey: 'Options' },
                    ],
                    searchOptions: {
                        allow: false
                    }
                }
            }}

            subtypes={[
                SUBTYPES.NEWS,
                SUBTYPES.RESEARCH,
                SUBTYPES.SYSTEM,
            ]}

            {...restOfPageProps}
        />
    )
}

const AssetsPage: React.FC<AssetsPageProps> = (pageProps) => {
    return (
        <PageContainer
            title={renderRouteOptions(Routes.ASSETS).title}
        >
            <AssetsPageContent {...pageProps} />
        </PageContainer>
    )
}

export default AssetsPage